import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useState } from "react"
import type { Resolver } from "react-hook-form"
import { FaEdit } from "react-icons/fa"

import { useUpdateSession } from "@/hooks/api/useSessions"
import { usePermissions } from "@/hooks/usePermissions"
import type { EditSessionProps, SessionUpdateInput } from "@/types/session"
import { sessionUpdateSchema } from "@/types/session"
import { Button } from "@chakra-ui/react"

import SessionFormDialog from "./SessionFormDialog"

const EditSession = memo(
  ({ session, onSuccess, onCancel }: EditSessionProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const updateSessionMutation = useUpdateSession()
    const { hasPermission } = usePermissions()

    const defaultValues = useMemo<SessionUpdateInput>(
      () => ({
        name: session.name ?? "",
        slug: session.slug ?? "",
        description: session.description ?? "",
        session_type: session.session_type ?? null,
        start_week: session.start_week ?? "",
        end_week: session.end_week ?? "",
        registration_opens: session.registration_opens ?? "",
        registration_closes: session.registration_closes ?? "",
        early_bird_deadline: session.early_bird_deadline ?? "",
        makeup_classes_allowed: session.makeup_classes_allowed ?? null,
        no_class_dates: session.no_class_dates
          ? JSON.stringify(session.no_class_dates)
          : "",
        is_active: session.is_active ?? false,
        is_published: session.is_published ?? false,
        notes: session.notes ?? "",
      }),
      [session],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: SessionUpdateInput) => {
      await updateSessionMutation.mutateAsync({
        sessionId: session.id,
        sessionData: data,
      })
      onSuccess?.()
    }

    // Don't render if user doesn't have permission
    if (!hasPermission("sessions:update")) {
      return null
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          aria-label={`Edit session: ${session.id}`}
        >
          <FaEdit fontSize="14px" aria-hidden="true" />
          Edit
        </Button>

        <SessionFormDialog
          isOpen={isOpen}
          title="Edit Season"
          submitLabel="Update Season"
          /* TODO: zodResolver generic mismatch with react-hook-form Resolver type */
          resolver={zodResolver(sessionUpdateSchema) as Resolver<SessionUpdateInput>}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={updateSessionMutation.isPending}
          loadingText="Updating..."
        />
      </>
    )
  },
)

EditSession.displayName = "EditSession"

export default EditSession
export type { EditSessionProps }
